<template>
    <page-container>
        <page-header>
            Flows

            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="lg"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <flows-overview v-show="showOverview" class="mb-14"></flows-overview>

        <flows-filters
            v-model="filters"
            @apply="applyFilters"
            @reset="resetFilters"
            :list="list"
            :loading="loading"
        ></flows-filters>

        <page-callout class="my-12" section="form-submission" shadow />

        <div v-if="loading" class="text-center py-10">
            <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
        </div>
        <template v-else>
            <div class="flex flex-col gap-4">
                <flow-card
                    v-for="item in filteredList"
                    :key="item.flow_id"
                    :modelValue="item"
                    @deleted="remove(item.flow_id)"
                ></flow-card>
            </div>
            <empty-state v-if="!list.length">
                No flows yet.
                <new-flow-button href="/automation/flows/new" :disabled="!!(loading || list.length >= FLOWS_LIMIT)">
                </new-flow-button>
            </empty-state>
            <empty-state v-else-if="!filteredList.length" @reset="resetFilters" />
        </template>
    </page-container>
</template>

<script setup>
import { faCircleChevronDown, faCircleChevronUp, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxIconLoading } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { useToasted } from '~/composables/useToasted';
import { flowsClient } from '~/services';
import FlowCard from './FlowCard.vue';
import FlowsFilters from './FlowsFilters.vue';
import FlowsOverview from './FlowsOverview.vue';
import NewFlowButton from './components/NewFlowButton.vue';
import PageCallout from '~/components/general/PageCallout.vue';

const FLOWS_LIMIT = 20;

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const showOverview = ref(true);

const defaultFilters = () => ({
    query: '',
    sort_field: 'created_at',
    sort_dir: 'desc',
    category: '',
});

const filters = ref(defaultFilters());

const loading = ref(true);
const list = ref([]);
const filteredList = ref([]);

const fetchRecords = async () => {
    try {
        loading.value = true;
        const response = await flowsClient.list(teamId);
        list.value = response.data.data;
        applyFilters();
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const applyFilters = () => {
    filteredList.value = list.value
        .filter((item) => {
            const name = item.name.toLocaleLowerCase().includes(filters.value.query.toLocaleLowerCase());
            const category = !filters.value.category?.length || filters.value.category === item.category;

            return name && category;
        })
        .sort((a, b) => {
            if (filters.value.sort_field === 'name') {
                return filters.value.sort_dir === 'desc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            } else if (['created_at', 'updated_at'].includes(filters.value.sort_field)) {
                return filters.value.sort_dir === 'desc'
                    ? new Date(b[filters.value.sort_field]) - new Date(a[filters.value.sort_field])
                    : new Date(a[filters.value.sort_field]) - new Date(b[filters.value.sort_field]);
            } else {
                return 0;
            }
        });
};

const resetFilters = () => {
    filters.value = defaultFilters();
    applyFilters();
};

const remove = async (flowId) => {
    list.value = list.value.filter((item) => item.flow_id !== flowId);
    filteredList.value = filteredList.value.filter((item) => item.flow_id !== flowId);
};

onMounted(() => {
    fetchRecords();
});
</script>
